"use client";

import * as LabelPrimitive from "@radix-ui/react-label";
import { Slot, type SlotProps } from "@radix-ui/react-slot";
import { hasValue } from "@woonstad/helpers-utils";
import { createContext, forwardRef, useContext, useId, useMemo } from "react";
import type {
  Control,
  ControllerFieldState,
  ControllerProps,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  Path,
  UseFormSetValue,
} from "react-hook-form";
import { Controller, useController } from "react-hook-form";

import { Popover, PopoverProps } from "../popover/Popover";
import { Typography } from "../typography/Typography";
import { FeedbackError } from "./feedback/feedback-error";

type FormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  render: NonNullable<ControllerProps<TFieldValues, TName>["render"]>;
  control: Control<TFieldValues, any, any>;
  description?: string | React.ReactNode;
  label?: string | React.ReactElement;
  largeLabel?: boolean;
  name: NonNullable<ControllerProps<TFieldValues, TName>["name"]>;
  setValue: UseFormSetValue<TFieldValues>;
  disabled?: boolean;
  shouldUnregister?: boolean;
  hideErrorMessage?: boolean;
  testid?: string;
  popover?: LabelPopoverProps;
  notRequired?: boolean | string;
};

// Errors in fields can be a single error or an array of errors.
type FieldState = Omit<ControllerFieldState, "error"> & {
  error: ControllerFieldState["error"] | Array<ControllerFieldState["error"]>;
};

type FormFieldContextValue<TFieldValues extends FieldValues> = {
  id: string;
  name: string;
  field?: ControllerRenderProps<TFieldValues, Path<TFieldValues>>;
  fieldState: FieldState;
  setValue: UseFormSetValue<TFieldValues>;
};

const FormFieldContext = createContext<FormFieldContextValue<any> | undefined>(
  undefined,
);

export const useFormField = () => {
  const fieldContext = useContext(FormFieldContext);

  if (!fieldContext)
    throw new Error("useFormField must be used within a FormField.");

  return {
    ...fieldContext,
    formItemId: `${fieldContext.id}-form-item`,
    formDescriptionId: `${fieldContext.id}-form-item-description`,
    formMessageId: `${fieldContext.id}-form-item-message`,
  };
};

/**
 * Wrapper around the <Slot /> component from Radix UI. Injects aria attributes and error states.
 */
const FormControl = forwardRef(
  (
    props: Omit<SlotProps & React.RefAttributes<HTMLElement>, "ref">,
    ref?: React.Ref<HTMLElement>,
  ) => {
    const { fieldState, formDescriptionId, formMessageId } = useFormField();

    return (
      <div
        aria-describedby={
          !fieldState.error
            ? `${formDescriptionId}`
            : `${formDescriptionId} ${formMessageId}`
        }
        aria-invalid={!!fieldState.error}
      >
        <Slot ref={ref} {...props} />
      </div>
    );
  },
);

FormControl.displayName = "FormControl";

export type LabelPopoverProps = Omit<PopoverProps, "trigger">;

/* Label Popover */
const LabelPopover = (props: LabelPopoverProps) => {
  return (
    <Popover
      {...props}
      trigger={(title) => (
        <button
          type="button"
          className="ml-2 inline font-label-small-mobile text-label-small-mobile leading-label-regular-mobile text-link-enabled-on-light underline sm:font-label-small-desktop sm:text-label-small-desktop sm:leading-label-regular-desktop"
        >
          {title}
        </button>
      )}
    />
  );
};

/**
 * Wrapper around the <Label /> component from Radix UI. Links the label to the form field.
 */
const FormLabel = forwardRef(
  (
    {
      label,
      testid,
      popover,
      notRequired,
      largeLabel,
    }: Pick<
      FormFieldProps,
      "label" | "largeLabel" | "testid" | "popover" | "notRequired"
    >,
    ref?: React.Ref<HTMLParagraphElement>,
  ) => {
    const { fieldState, formItemId } = useFormField();

    if (!label) return null;

    return (
      <div className={largeLabel ? "mb-4" : "mb-2"}>
        <LabelPrimitive.Root asChild htmlFor={formItemId}>
          <Typography
            textColorClassname={
              fieldState?.error ? "text-label-error" : "text-label-enabled"
            }
            variant={largeLabel ? "title-body" : "label-regular"}
            type="label"
            data-testid={testid ? `${testid}Label` : undefined}
            className="inline"
          >
            {label}
            {notRequired && (
              <Typography
                variant="label-small"
                className="ml-2 text-form-optional-text"
                type="span"
              >
                (
                {typeof notRequired === "string"
                  ? notRequired
                  : "niet verplicht"}
                )
              </Typography>
            )}
          </Typography>
        </LabelPrimitive.Root>
        {hasValue(popover) && <LabelPopover {...popover} />}
      </div>
    );
  },
);

FormLabel.displayName = "FormLabel";

/**
 * Linked to the input element using aria-describedby.
 */
const FormDescription = forwardRef(
  (
    { description, testid }: Pick<FormFieldProps, "description" | "testid">,
    ref?: React.Ref<HTMLParagraphElement>,
  ) => {
    const { formDescriptionId } = useFormField();

    if (!description) return null;

    return (
      <Typography
        ref={ref}
        className="mt-2"
        id={formDescriptionId}
        textColorClassname="text-input-hint"
        variant="label-small"
        data-testid={testid}
        type="p"
      >
        {description}
      </Typography>
    );
  },
);

FormDescription.displayName = "FormDescription";

/**
 * The error message for the form field. Can be used to display error messages within the form field.
 * Used in the upload component to show errors for individual files.
 */
export const FormMessage = forwardRef(
  (
    { id, errorBody }: { id: string; errorBody?: string },
    ref?: React.Ref<HTMLParagraphElement>,
  ) => {
    if (!errorBody) return null;

    return (
      <div className="mt-2" ref={ref} id={id}>
        <FeedbackError message={errorBody} />
      </div>
    );
  },
);

FormMessage.displayName = "FormMessage";

/**
 * Used for displaying form error or success messages on the whole field.
 */
export const FormMessageField = forwardRef(
  (_, ref?: React.Ref<HTMLParagraphElement>) => {
    const { fieldState, formMessageId } = useFormField();

    if (
      !fieldState?.error ||
      // The fieldState.error can be an array too because some (the Upload component) fields can have multiple errors.
      // In that case we don't want to show the error message here - but in the upload component.
      Array.isArray(fieldState?.error)
    ) {
      return null;
    }

    return (
      <FormMessage id={formMessageId} errorBody={fieldState?.error?.message} />
    );
  },
);

FormMessageField.displayName = "FormMessageField";

/*
 * FormField creates a FormFieldContext in which the form field's state is stored.
 * It also acts as a wrapper around react-hook-form's <Controller /> component.
 */
export const FormField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: FormFieldProps<TFieldValues, TName>,
) => {
  const {
    name,
    label,
    notRequired,
    description,
    control,
    render,
    setValue,
    disabled,
    shouldUnregister,
    hideErrorMessage,
    testid,
    popover,
    largeLabel,
  } = props;

  const id = useId();

  const { field, fieldState } = useController({
    name: name,
    control: control,
    disabled: disabled,
    shouldUnregister: shouldUnregister,
  });

  const extendedRender: ControllerProps<TFieldValues, TName>["render"] = (
    ...args
  ) => {
    return (
      <div className="flex flex-col">
        <FormLabel
          label={label}
          testid={testid}
          popover={popover}
          notRequired={notRequired}
          largeLabel={largeLabel}
        />
        <FormControl>{render(...args)}</FormControl>
        {!hideErrorMessage && <FormMessageField />}
        <FormDescription
          description={description}
          testid={testid ? `${testid}Description` : undefined}
        />
      </div>
    );
  };

  const contextValue = useMemo<FormFieldContextValue<TFieldValues>>(
    () => ({
      name: name,
      id: id,
      // @TODO change this FormField into a factory to get all the typing correct in here
      field: field as any,
      fieldState: fieldState as FieldState,
      setValue: setValue,
    }),
    [field, fieldState, id, name, setValue],
  );

  return (
    <FormFieldContext.Provider value={contextValue}>
      <Controller control={control} name={name} render={extendedRender} />
    </FormFieldContext.Provider>
  );
};
